import routes from 'constants/routes';
import { ISort } from 'features/common/types';
import { SalesOrderFiltersState } from '../components/SaleOrderList/SalesOrderFilters/types';

export type SalesOrderDepartureSort = 'desc' | 'asc';

const RESERVED_PARAMS = new Set(['search', 'page', 'size', 'sort']);

const BOOLEAN_FILTER_KEYS = new Set(['hasPendingPayAmount', 'customerEmpty', 'isBitrixDeal', 'isReturnDateReached']);

const INTEGER_FILTER_KEYS = new Set(['dealId', 'orderNumber', 'quotationId', 'channelId']);

const FLOAT_FILTER_KEYS = new Set([
	'salesAmountFrom',
	'salesAmountTo',
	'totalCostFrom',
	'totalCostTo',
	'grossProfitFrom',
	'grossProfitTo',
	'chargedAmountFrom',
	'chargedAmountTo',
	'pendingPayAmountFrom',
	'pendingPayAmountTo',
]);

export const SALES_ORDER_LIST_DEFAULT_SORT: ISort[] = [{ field: 'created', order: 'desc' }];

export interface SalesOrderListUrlState {
	search: string;
	page: number;
	size: number;
	sortFields: ISort[];
	filterApplied: Record<string, string | number | boolean>;
	initialFilters: Partial<SalesOrderFiltersState>;
}

export interface BuildSalesOrderListUrlParams {
	customerCompleteName?: string;
	hasPendingPayAmount?: boolean;
	departureSort?: SalesOrderDepartureSort;
}

export const normalizeSalesOrderListSearch = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

const sortFromParam = (value: string | null): ISort[] => {
	if (!value) return SALES_ORDER_LIST_DEFAULT_SORT;
	const primary = value.split(',')[0]?.trim();
	if (!primary) return SALES_ORDER_LIST_DEFAULT_SORT;
	if (primary.startsWith('-')) {
		return [{ field: primary.slice(1), order: 'desc' }];
	}
	return [{ field: primary, order: 'asc' }];
};

const sortToParam = (sortFields: ISort[]): string | undefined => {
	const primary = sortFields[0];
	if (!primary) return undefined;
	return primary.order === 'desc' ? `-${primary.field}` : primary.field;
};

const parseEndpointFilterValue = (key: string, value: string): string | number | boolean => {
	if (BOOLEAN_FILTER_KEYS.has(key)) return value === 'true';
	if (INTEGER_FILTER_KEYS.has(key)) {
		const parsed = parseInt(value, 10);
		return Number.isNaN(parsed) ? value : parsed;
	}
	if (FLOAT_FILTER_KEYS.has(key)) {
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? value : parsed;
	}
	return value;
};

const mapEndpointToInitialFilters = (
	key: string,
	parsed: string | number | boolean,
	initialFilters: Partial<SalesOrderFiltersState>,
): void => {
	switch (key) {
		case 'customerCompleteName':
			initialFilters.customerCompleteName = String(parsed);
			break;
		case 'mainPassengerCompleteName':
			initialFilters.mainPassengerCompleteName = String(parsed);
			break;
		case 'dealId':
			initialFilters.dealId = String(parsed);
			break;
		case 'segmentType':
			initialFilters.segmentType = String(parsed);
			break;
		case 'hasPendingPayAmount':
			initialFilters.hasPendingPayAmount = Boolean(parsed);
			break;
		case 'customerEmpty':
			initialFilters.customerEmpty = Boolean(parsed);
			break;
		case 'isBitrixDeal':
			initialFilters.isBitrixDeal = Boolean(parsed);
			break;
		case 'isReturnDateReached':
			initialFilters.isReturnDateReached = Boolean(parsed);
			break;
		case 'invoicingStatuses':
			initialFilters.invoicingStatuses = String(parsed)
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			break;
		default:
			break;
	}
};

export const parseSalesOrderListState = (search: string): SalesOrderListUrlState => {
	const params = new URLSearchParams(normalizeSalesOrderListSearch(search));
	const filterApplied: Record<string, string | number | boolean> = {};
	const initialFilters: Partial<SalesOrderFiltersState> = {};

	params.forEach((value, key) => {
		if (RESERVED_PARAMS.has(key) || !value) return;
		const parsed = parseEndpointFilterValue(key, value);
		filterApplied[key] = parsed;
		mapEndpointToInitialFilters(key, parsed, initialFilters);
	});

	return {
		search: params.get('search') ?? '',
		page: Math.max(1, Number(params.get('page')) || 1),
		size: Math.max(1, Number(params.get('size')) || 10),
		sortFields: sortFromParam(params.get('sort')),
		filterApplied,
		initialFilters,
	};
};

export const buildSalesOrderListUrl = ({
	customerCompleteName,
	hasPendingPayAmount,
	departureSort,
}: BuildSalesOrderListUrlParams): string => {
	const params = new URLSearchParams();

	if (customerCompleteName?.trim()) {
		params.set('customerCompleteName', customerCompleteName.trim());
	}

	if (hasPendingPayAmount === true) {
		params.set('hasPendingPayAmount', 'true');
	} else if (hasPendingPayAmount === false) {
		params.set('hasPendingPayAmount', 'false');
	}

	if (departureSort) {
		params.set('sort', departureSort === 'desc' ? '-travelDepartureDate' : 'travelDepartureDate');
	}

	const query = params.toString();
	return query ? `${routes.salesOrder.list}?${query}` : routes.salesOrder.list;
};

export const buildSalesOrderListSearch = (state: {
	search?: string;
	page?: number;
	size?: number;
	sortFields?: ISort[];
	filterApplied?: Record<string, string | number | boolean>;
}): string => {
	const params = new URLSearchParams();

	if (state.search?.trim()) {
		params.set('search', state.search.trim());
	}

	if (state.page && state.page > 1) {
		params.set('page', String(state.page));
	}

	if (state.size && state.size !== 10) {
		params.set('size', String(state.size));
	}

	const sortParam = state.sortFields ? sortToParam(state.sortFields) : undefined;
	if (sortParam && sortParam !== '-created') {
		params.set('sort', sortParam);
	}

	Object.entries(state.filterApplied ?? {}).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') return;
		params.set(key, String(value));
	});

	return params.toString();
};
